import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { CURRENT_TENANT } from '../utils/env';

const TenantContext = createContext(null);

/**
 * TenantProvider - Provides tenant configuration throughout the app.
 *
 * Fetches tenant config from the API on mount, which includes:
 * - tenant_id: The current tenant identifier
 * - name: Display name for the tenant
 * - settings: Feature flags and custom settings
 * - branding: Logo, colors, and display customizations
 * - multi_tenant_enabled: Whether multi-tenant mode is active
 */
export const TenantProvider = ({ children }) => {
  const [tenantConfig, setTenantConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch tenant configuration from API
  const fetchTenantConfig = useCallback(async () => {
    try {
      const response = await api.get('/tenant/config');
      setTenantConfig(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      console.warn('Failed to fetch tenant config:', err);
      // Set default config on error
      setTenantConfig({
        tenant_id: CURRENT_TENANT || 'default',
        name: 'Uni-Fund',
        multi_tenant_enabled: false,
        settings: {},
        branding: null,
      });
      setError(err.message);
      return null;
    }
  }, []);

  // Fetch tenant config on mount
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchTenantConfig();
      setLoading(false);
    };
    init();
  }, [fetchTenantConfig]);

  // Get a tenant setting with optional fallback
  const getTenantSetting = useCallback((key, defaultValue = null) => {
    if (!tenantConfig?.settings) return defaultValue;
    return tenantConfig.settings[key] ?? defaultValue;
  }, [tenantConfig]);

  // Check if a feature is enabled for this tenant
  const isFeatureEnabled = useCallback((feature) => {
    if (!tenantConfig?.settings) return false;
    return Boolean(tenantConfig.settings[feature]);
  }, [tenantConfig]);

  // Get branding configuration
  const getBranding = useCallback(() => {
    return tenantConfig?.branding || {
      logo_url: null,
      primary_color: null,
      display_name: null,
    };
  }, [tenantConfig]);

  // Get tenant display name
  const getTenantName = useCallback(() => {
    return tenantConfig?.name || 'Uni-Fund';
  }, [tenantConfig]);

  // Check if multi-tenant mode is enabled
  const isMultiTenant = useCallback(() => {
    return Boolean(tenantConfig?.multi_tenant_enabled);
  }, [tenantConfig]);

  const value = {
    // State
    tenantConfig,
    tenantId: tenantConfig?.tenant_id || CURRENT_TENANT || 'default',
    tenantName: getTenantName(),
    loading,
    error,

    // Methods
    fetchTenantConfig,
    getTenantSetting,
    isFeatureEnabled,
    getBranding,
    getTenantName,
    isMultiTenant,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

// Hook to use tenant context
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// Convenience hook for getting a specific setting
export const useTenantSetting = (key, defaultValue = null) => {
  const { getTenantSetting } = useTenant();
  return getTenantSetting(key, defaultValue);
};

// Convenience hook for branding
export const useTenantBranding = () => {
  const { getBranding } = useTenant();
  return getBranding();
};

export default TenantContext;
